import React from 'react';
import ReactDOM from 'react-dom';

/**
 * Portal component that renders children outside the main DOM hierarchy.
 * Useful for modals, tooltips, and overlays that need to escape parent styling constraints.
 *
 * @example
 * ```tsx
 * <Portal>
 *   <div className="modal">Modal content</div>
 * </Portal>
 * ```
 *
 * @param children - React nodes to render in the portal
 */
const Portal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mounted, setMounted] = React.useState(false);
  const el = React.useRef(document.createElement('div'));

  React.useEffect(() => {
    setMounted(true);
    document.body.appendChild(el.current);
    return () => {
      // Check if the element is still a child before removing it
      if (el.current && el.current.parentNode === document.body) {
        document.body.removeChild(el.current);
      }
    };
  }, []);

  if (!mounted) return null;
  return ReactDOM.createPortal(children, el.current);
};

export default Portal;
